import { Component, Input, OnChanges, QueryList, SimpleChanges, ViewChildren, ChangeDetectionStrategy } from "@angular/core";
import { FormsModule } from "@angular/forms";
import {
  DocumentType,
  languageLevelGermanCertificateMapping,
  languageSkillLabelMapping,
  UTCDate,
} from "@ankaadia/ankaadia-shared";
import {
  CertifiedEducationExamFragment,
  CertifiedEducationExamGroupFragment,
  ExamModuleType,
  StaticDataType,
} from "@ankaadia/graphql";
import { AnkaadiaPresetOverride } from "@ankaadia/ui";
import { compact, first, orderBy } from "lodash";
import { DatePicker } from "primeng/datepicker";
import { FloatLabel } from "primeng/floatlabel";
import { InputText } from "primeng/inputtext";
import { NewUiCandidateDocumentDateComponent } from "../../features/candidate-document-metadata/metadata-wrapper-components/new-ui-candidate-document-date.component";
import { AnyCandidateForm } from "../../features/candidate-form/candidate-form.model";
import { DeepReplaceDateWithTime } from "../../shared/date-time/deep-replace-date-with-time.type";
import { NewUiWrapperReadonlyComponent } from "../../shared/form/wrapper-readonly/new-ui-wrapper-readonly.component";
import { AppDatePipe, AppDateTimePipe } from "../../shared/pipes/date.pipe";
import { EnumPipe } from "../../shared/pipes/enum.pipe";
import { NewUiCalendarUtcFixDirective } from "../../shared/primeng/calendar-utc-fix/new-ui-calendar-utc-fix.directive";
import { TranslateDirective } from "../../shared/transloco/translate.directive";

@Component({
  selector: "new-ui-formly-certified-education-exams-entry",
  templateUrl: "./new-ui-formly-certified-education-exams-entry.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TranslateDirective,
    FormsModule,
    AppDatePipe,
    AppDateTimePipe,
    NewUiCalendarUtcFixDirective,
    EnumPipe,
    NewUiCandidateDocumentDateComponent,
    FloatLabel,
    InputText,
    DatePicker,
    NewUiWrapperReadonlyComponent,
  ],
})
export class NewUiFormlyCertifiedEducationExamsEntryComponent implements OnChanges {
  readonly StaticDataType = StaticDataType;
  readonly languageSkillLabelMapping = languageSkillLabelMapping;

  @Input() examGroup: CertifiedEducationExamGroupFragment;
  @Input() candidateId: string;
  @Input() organizationId: string;
  @Input() form: AnyCandidateForm;
  @Input() disabled: boolean;
  @Input() readonly: boolean;
  @Input() dt: AnkaadiaPresetOverride;
  @Input({ required: true }) showDocumentCompletion?: boolean;

  @ViewChildren(NewUiCandidateDocumentDateComponent)
  issueDates: QueryList<NewUiCandidateDocumentDateComponent>;

  exams: DeepReplaceDateWithTime<CertifiedExamData>[] = [];

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.examGroup) {
      this.exams = (this.examGroup?.exams ?? []).map((x) => this.toCertifiedExamData(x));
    }
  }

  get latestIssueDate(): UTCDate | null {
    return first(
      orderBy(
        compact(
          (this.issueDates ?? []).map(
            (x: NewUiCandidateDocumentDateComponent) => x.shownDate
          )
        ),
        (x) => x,
        "desc"
      )
    );
  }

  private toCertifiedExamData(exam: CertifiedEducationExamFragment): DeepReplaceDateWithTime<CertifiedExamData> {
    return {
      ...exam,
      documentType: this.getSelectedSet(exam.languageLevel),
    } as DeepReplaceDateWithTime<CertifiedExamData>;
  }

  private getSelectedSet(skillLevel: string): DocumentType {
    if (!skillLevel) return "LANGCERT";

    return languageLevelGermanCertificateMapping[skillLevel] ?? "LANGCERT";
  }

  protected readonly ExamModuleType = ExamModuleType;
}

interface DocumentData {
  documentType: DocumentType;
}

type CertifiedExamData = CertifiedEducationExamFragment & DocumentData;
